import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';
import { useHypoteseListe, useHypoteseTellinger, type HypoteseFilter, type HypothesisCounts, type HypothesisStatusType } from '../api/useHypoteseBehandling.ts';
import { useSearchParams } from 'react-router';
import { useHypoteseConfig } from '../api/useHypotheses.ts';
import { useTilgang } from '../skall/tilgang.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Arbeidsflate } from './Arbeidsflate.tsx';
import { sidenTekst } from './setning.ts';
import { Tekstknapp, Varsel } from './deler.tsx';

type Filter = 'alle' | 'vurdering' | 'testes' | 'konkludert' | 'parkert' | 'avsluttet';

/**
 * Filteret er segmenter på status (W9, `hypotese-innsending.jsx` FILTER). Kontrakten filtrerer på
 * ÉN status eller ÉN statusType; segmentene er statusType, så hver status står i nøyaktig ett:
 * «Testes» er valgt og testes (`active`), «Parkert» er parkert og på pause (`waiting`),
 * «Avsluttet» er avvist, stoppet og trukket (`closed`). Wireframen sier ikke hvor Valgt og På
 * pause hører hjemme (rapportert som avvik).
 */
const FILTER: ReadonlyArray<{ verdi: Filter; navn: string; ikon: string; type?: HypothesisStatusType }> = [
  { verdi: 'alle', navn: 'Alle', ikon: 'checkmark-outline' },
  { verdi: 'vurdering', navn: 'Til vurdering', ikon: 'hourglass-outline', type: 'reviewing' },
  { verdi: 'testes', navn: 'Testes', ikon: 'flask-outline', type: 'active' },
  { verdi: 'konkludert', navn: 'Konkludert', ikon: 'add-circle-outline', type: 'complete' },
  { verdi: 'parkert', navn: 'Parkert', ikon: 'bookmark-outline', type: 'waiting' },
  { verdi: 'avsluttet', navn: 'Avsluttet', ikon: 'close-circle-outline', type: 'closed' },
];

/** Alle hypoteser i rommet som ikke er utkast — tjeneren teller aldri utkast (H20). */
export function iAlt(c: HypothesisCounts): number {
  return c.received + c.reviewing + c.parked + c.active + c.paused + c.confirmed + c.partial + c.refuted + c.stopped + c.rejected + c.withdrawn;
}

/** Tallet på hvert segment (DD-75): `GET /counts` lagt sammen per gruppe — aldri telt i klienten fra lista. */
export function iGruppen(c: HypothesisCounts, type: HypothesisStatusType | undefined): number {
  switch (type) {
    case 'reviewing':
      return c.received + c.reviewing;
    case 'active':
      return c.active;
    case 'complete':
      return c.confirmed + c.partial + c.refuted;
    case 'waiting':
      return c.parked + c.paused;
    case 'closed':
      return c.rejected + c.stopped + c.withdrawn;
    default:
      return iAlt(c);
  }
}

/**
 * Registeret (H2.3; W9 og M10b, kravlisten `hypotese-innsending.jsx` SideRegisteret) — lesbart
 * for alle i rommet, uten notater for bidragsyter. Lista står over hele flaten, og en hypotese
 * åpnes alene med «Registeret» som vei tilbake (konseptets skjerm 6).
 *
 * Tellelinjen er ÉN setning i body over lista: antallet, så `GET /counts` ordrett. Tellinger,
 * aldri prosent, aldri en forside (DD-01, H20). «151 hypoteser siden mars» leser måneden fra
 * `counts.since` (eldste innsending), med årstallet når det ikke er i år; uten `since` (tomt
 * register) står setningen uten «siden …».
 */
export function HRegister({ nr }: { nr: number | null }) {
  const [filter, setFilter] = useState<Filter>('alle');
  // DD-74: «Frist først · Nyest først» i adressen, som innboksen — og med «Menneskesatte tall» på,
  // én faktor om gangen (H3.2, svar 4). Aldri en sum, aldri to faktorer.
  const config = useHypoteseConfig();
  const tall = Boolean(config.data?.scoresEnabled);
  const [sort, setSort] = useRegisterRekkefolge(tall);
  const spoerring = useMemo<HypoteseFilter>(() => {
    const type = FILTER.find((f) => f.verdi === filter)?.type;
    return { ...(type ? { statusType: type } : {}), ...(sort !== 'frist' ? { sort } : {}) };
  }, [filter, sort]);
  const faktor = sort === 'impact' || sort === 'confidence' || sort === 'effort' ? sort : undefined;
  const liste = useHypoteseListe(spoerring);
  const tellinger = useHypoteseTellinger();
  // Et utkast er bare innsenderens eget, og hører til «Mine hypoteser» — aldri registeret.
  const rader = useMemo(() => (liste.data?.pages.flatMap((p) => p.items) ?? []).filter((h) => h.status !== 'utkast'), [liste.data]);
  const c = tellinger.data;
  const siden = c?.since ? sidenTekst(c.since) : null;
  const romTomt = c !== undefined && iAlt(c) === 0 && filter === 'alle' && liste.isSuccess && rader.length === 0;
  // H2.8 (DD-80): ved «Ny hypotese», bare for eier — tjeneren vokter med hypoteser:eier.
  const eier = useTilgang().eier;
  const leggInn = eier ? (
    <Link to="/h/legg-inn" className="panel-knapp-sekundaer" data-testid="hr-legg-inn">
      <Ikon navn="add-outline" />
      Legg inn fra tidligere kanal
    </Link>
  ) : null;

  const tellelinje = c ? (
    <p className="hb-tellelinje" data-testid="hr-tellelinje">
      <b>{iAlt(c)}</b> hypoteser{siden ? ` ${siden}` : ''}: <b>{c.confirmed}</b> bekreftet · <b>{c.partial}</b> delvis · <b>{c.refuted}</b> avkreftet · <b>{c.active}</b> valgt uten konklusjon
    </p>
  ) : tellinger.isError ? (
    <p className="hb-dempet">Tallene kunne ikke hentes.</p>
  ) : null;

  const tom = romTomt ? (
    <div className="hb-tom" data-testid="hb-tom">
      <h1 className="panel-h1">Registeret er tomt</h1>
      <p className="hb-dempet">Hver hypotese som sendes inn, står her med svar og dato — også de som blir avvist.</p>
      <div className="hr-tom-knapper">
        <Link to="/h/ny" className="panel-knapp panel-tomrute-knapp">
          Ny hypotese
          <Ikon navn="chevron-forward-outline" />
        </Link>
        {leggInn}
      </div>
    </div>
  ) : null;

  return (
    <Arbeidsflate
      tittel="Registeret"
      base="/h/registeret"
      nr={nr}
      alene
      kartTittel="Registeret"
      grupper={[{ navn: null, rader }]}
      faktor={faktor}
      laster={liste.isPending}
      feil={
        liste.isError ? (
          <Varsel handling={<Tekstknapp onClick={() => void liste.refetch()}>Prøv igjen</Tekstknapp>}>Kunne ikke hente registeret. Ingenting er endret.</Varsel>
        ) : filter !== 'alle' && liste.isSuccess && rader.length === 0 ? (
          <p role="status" className="hb-dempet">
            Ingen hypoteser med dette filteret. <Tekstknapp onClick={() => setFilter('alle')}>Vis alle</Tekstknapp>
          </p>
        ) : null
      }
      topp={
        <>
          {leggInn && !romTomt ? <div className="hr-handlinger">{leggInn}</div> : null}
          {tellelinje}
          <Segmenter verdi={filter} onVelg={setFilter} tellinger={c} />
          <RegisterRekkefolge sort={sort} tall={tall} onVelg={setSort} />
        </>
      }
      etter={
        liste.hasNextPage ? (
          <div className="hb-liste-melding">
            <Tekstknapp onClick={() => void liste.fetchNextPage()}>{liste.isFetchingNextPage ? 'Henter …' : 'Vis flere'}</Tekstknapp>
          </div>
        ) : null
      }
      tom={tom}
    />
  );
}

/**
 * Segmentene som radiogruppe (ett valg, piltastene flytter), med statusens ikon, ordet og tallet
 * for gruppen (DD-75, 2026-10-09 — konseptet hadde tall bare på «Til vurdering»). Null vises som
 * «0»; tallet er en del av segmentets navn, så skjermleseren hører «Parkert 2».
 */
function Segmenter({ verdi, onVelg, tellinger }: { verdi: Filter; onVelg: (f: Filter) => void; tellinger: HypothesisCounts | undefined }) {
  const knapper = useRef<Array<HTMLButtonElement | null>>([]);
  const flytt = (e: KeyboardEvent, i: number) => {
    const steg = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!steg) return;
    e.preventDefault();
    const neste = (i + steg + FILTER.length) % FILTER.length;
    onVelg(FILTER[neste]!.verdi);
    knapper.current[neste]?.focus();
  };
  return (
    <div role="radiogroup" aria-label="Filter" className="panel-filter-segment hb-segmenter">
      {FILTER.map((f, i) => {
        const paa = verdi === f.verdi;
        return (
          <button
            key={f.verdi}
            ref={(el) => {
              knapper.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={paa}
            tabIndex={paa ? 0 : -1}
            className="panel-filter-valg"
            onClick={() => onVelg(f.verdi)}
            onKeyDown={(e) => flytt(e, i)}
            data-testid={`hr-filter-${f.verdi}`}
          >
            <Ikon navn={paa ? 'checkmark-outline' : f.ikon} />
            {f.navn}
            {/* Mellomrommet gir det tilgjengelige navnet «Parkert 2»; i flex-raden tegnes det ikke. */}
            {tellinger ? <> <span className="hb-segment-tall">{iGruppen(tellinger, f.type)}</span></> : null}
          </button>
        );
      })}
    </div>
  );
}

type Rekkefolge = NonNullable<HypoteseFilter['sort']>;
const REKKEFOLGE: ReadonlyArray<[Rekkefolge, string]> = [
  ['frist', 'Frist først'],
  ['nyest', 'Nyest først'],
  ['impact', 'Impact'],
  ['confidence', 'Confidence'],
  ['effort', 'Effort'],
];
const FAKTORER: Rekkefolge[] = ['impact', 'confidence', 'effort'];

/**
 * Registerets rekkefølge i adressen (`?sort=`, DD-74). Faktorene finnes bare med «Menneskesatte tall»
 * på: en adresse med en faktor mens bryteren er av, leses som «Frist først» — ukjent er strengest.
 */
function useRegisterRekkefolge(tall: boolean): [Rekkefolge, (s: Rekkefolge) => void] {
  const [params, setParams] = useSearchParams();
  const rå = params.get('sort') as Rekkefolge | null;
  const sort: Rekkefolge = rå === 'nyest' || (tall && rå !== null && FAKTORER.includes(rå)) ? rå : 'frist';
  const sett = (ny: Rekkefolge) =>
    setParams(
      (forrige) => {
        const neste = new URLSearchParams(forrige);
        if (ny === 'frist') neste.delete('sort');
        else neste.set('sort', ny);
        return neste;
      },
      { replace: true },
    );
  return [sort, sett];
}

/** «Frist først · Nyest først», og med bryteren på «Impact · Confidence · Effort» — ett valg, aldri to. */
function RegisterRekkefolge({ sort, tall, onVelg }: { sort: Rekkefolge; tall: boolean; onVelg: (s: Rekkefolge) => void }) {
  return (
    <div className="panel-filter-segment panel-rekkefolge" role="group" aria-label="Rekkefølge">
      {REKKEFOLGE.filter(([v]) => tall || !FAKTORER.includes(v)).map(([verdi, navn]) => (
        <button key={verdi} type="button" className="panel-filter-valg" aria-pressed={sort === verdi} onClick={() => onVelg(verdi)} data-testid={`rekkefolge-${verdi}`}>
          {navn}
        </button>
      ))}
    </div>
  );
}
